// Changement de mot de passe IMPOSÉ, à la première connexion d'un compte créé
// par l'exploitant.
//
// Même parti que `Login.tsx` : aucune route, l'écran remplace l'application
// entière tant que le drapeau est posé. Une route se contournerait en tapant
// une autre adresse, et on verrait alors une interface à moitié montée dont
// tous les appels d'API rendent 403.
//
// Ce n'est PAS cet écran qui protège quoi que ce soit : le garde du serveur
// refuse déjà tout le reste de l'API (cf. `estAutoriseSansChangement` dans
// web/auth.ts). Il rend seulement le refus compréhensible et actionnable.

import { useState } from 'react'
import { KeyRound, LogOut } from 'lucide-react'
import { ApiError, post } from '../lib/api'
import { useT } from '../lib/i18n'

/** Doit valoir `LONGUEUR_MOT_DE_PASSE` de `web/auth.ts`. Dupliqué et non
 *  importé : ce fichier est bundlé par Vite pour le navigateur, l'autre
 *  tourne sous Node. Le serveur reste l'autorité, cette
 *  valeur ne sert qu'à le dire avant l'aller-retour. */
const LONGUEUR_MINIMALE = 12

export function ChangerMotDePasse({ onChange, onDeconnexion }: { onChange: () => void; onDeconnexion: () => void }) {
  const t = useT()
  const [nouveau, setNouveau] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const [erreur, setErreur] = useState<string | null>(null)
  const [envoi, setEnvoi] = useState(false)

  async function soumettre(e: React.FormEvent) {
    e.preventDefault()
    setErreur(null)
    // Les deux contrôles que le client peut faire seul, faits ici pour ne pas
    // facturer un aller-retour et un scrypt à une faute de frappe. Le serveur
    // les refait, il reste seul juge.
    if (nouveau.length < LONGUEUR_MINIMALE) {
      setErreur(t('password.tooShort', { n: LONGUEUR_MINIMALE }))
      return
    }
    if (nouveau !== confirmation) {
      setErreur(t('password.mismatch'))
      return
    }
    setEnvoi(true)
    try {
      // Pas de mot de passe actuel : il vient d'être tapé pour ouvrir cette
      // session, et c'est le seul écran que ce compte peut atteindre. Le
      // serveur ne le redemande que pour un changement VOLONTAIRE.
      await post('/api/auth/password', { next: nouveau })
      onChange()
    } catch (err) {
      // Les refus prévisibles se disent dans la langue de l'interface ; le
      // message du serveur, en français, ne sert que pour l'imprévu.
      if (err instanceof ApiError && err.code === 'identique') setErreur(t('password.error.same'))
      else if (err instanceof ApiError && err.code === 'trop_court') setErreur(t('password.tooShort', { n: LONGUEUR_MINIMALE }))
      else setErreur(err instanceof ApiError ? err.message : t('password.error.generic'))
    } finally {
      setEnvoi(false)
    }
  }

  return (
    <div className="loginwrap">
      <form className="card logincard" onSubmit={soumettre}>
        <h1 className="loginh">{t('password.title')}</h1>
        <p className="loginsub">{t('password.intro')}</p>

        <label className="field-label" htmlFor="mdp-nouveau">{t('password.next')}</label>
        <input
          id="mdp-nouveau"
          className="input-field"
          type="password"
          autoComplete="new-password"
          autoFocus
          value={nouveau}
          onChange={(e) => setNouveau(e.target.value)}
          required
        />
        <p className="field-help">{t('password.rule', { n: LONGUEUR_MINIMALE })}</p>

        <label className="field-label" htmlFor="mdp-confirmation">{t('password.confirm')}</label>
        <input
          id="mdp-confirmation"
          className="input-field"
          type="password"
          autoComplete="new-password"
          value={confirmation}
          onChange={(e) => setConfirmation(e.target.value)}
          required
        />

        {/* `role="alert"` pour que l'échec soit annoncé, comme sur l'écran de
            connexion : sans lui un lecteur d'écran ne signale rien. */}
        {erreur && <p className="loginerr" role="alert">{erreur}</p>}

        <button className="btn-primary" type="submit" disabled={envoi}>
          <KeyRound size={16} />
          {envoi ? t('password.submitting') : t('password.submit')}
        </button>

        {/* La sortie de secours. Sans elle, quelqu'un qui n'a pas son mot de
            passe provisoire sous la main est coincé sur cet écran, session
            valide, sans même pouvoir se déconnecter. */}
        <button className="btn-secondary" type="button" onClick={onDeconnexion}>
          <LogOut size={16} />
          {t('password.logout')}
        </button>
      </form>
    </div>
  )
}
